import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import api from '../../services/api.ts';
import FormPage from '../../components/ui/FormPage.tsx';
import MoreOptions from '../../components/ui/MoreOptions.tsx';

const NovoLancamento: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const back = '/financeiro/fluxo-de-caixa';
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    type: 'out' as 'in' | 'out',
    description: '',
    amount: '',
    movementDate: new Date().toISOString().split('T')[0],
    cashAccountId: '',
    cashCategoryId: '',
    notes: '',
  });

  const { data: accounts } = useQuery('cash-accounts', async () => {
    const res = await api.get('/financeiro/contas-caixa');
    return res.data as Array<{ id: string; name: string; isDefault: boolean }>;
  });

  const { data: categories } = useQuery('cash-categories', async () => {
    const res = await api.get('/financeiro/categorias');
    return res.data as Array<{ id: string; name: string }>;
  });

  useEffect(() => {
    if (accounts?.length && !form.cashAccountId) {
      const def = accounts.find(a => a.isDefault) || accounts[0];
      setForm(f => ({ ...f, cashAccountId: String(def.id) }));
    }
  }, [accounts, form.cashAccountId]);

  const mutation = useMutation(
    async () => {
      await api.post('/financeiro/fluxo-de-caixa', {
        type: form.type,
        description: form.description.trim(),
        amount: Number(String(form.amount).replace(',', '.')),
        movementDate: form.movementDate,
        cashAccountId: form.cashAccountId ? Number(form.cashAccountId) : undefined,
        cashCategoryId: form.cashCategoryId ? Number(form.cashCategoryId) : undefined,
        origin: 'manual',
        notes: form.notes,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('cash-flow');
        navigate(back);
      },
      onError: () => setErrors({ submit: 'Não foi possível salvar o lançamento. Tente novamente.' }),
    }
  );

  const validate = () => {
    const next: Record<string, string> = {};
    if (!form.description.trim()) next.description = 'Informe a descrição do lançamento.';
    const amount = Number(String(form.amount).replace(',', '.'));
    if (!form.amount || !(amount > 0)) next.amount = 'Informe um valor maior que zero.';
    if (!form.movementDate) next.movementDate = 'Informe a data do lançamento.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) mutation.mutate();
  };

  return (
    <FormPage
      backTo={back}
      backLabel="Voltar ao fluxo de caixa"
      breadcrumb="Financeiro / Fluxo de caixa / Novo"
      title="Novo lançamento"
      subtitle="Registre o que já entrou ou saiu"
      hint="Campos essenciais primeiro. Use “Mais opções” só se precisar."
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={() => navigate(back)}>
            Cancelar
          </button>
          <button
            type="submit"
            form="novo-lancamento-form"
            className="btn btn-primary"
            disabled={mutation.isLoading}
          >
            {mutation.isLoading ? 'Salvando…' : 'Salvar lançamento'}
          </button>
        </>
      }
    >
      <form id="novo-lancamento-form" onSubmit={onSubmit} noValidate>
        {errors.submit && <div className="field-error" style={{ marginBottom: 12 }}>{errors.submit}</div>}
        <div className="form-section-title">O essencial</div>
        <div className="form-grid">
          <div className="field full">
            <label>Tipo</label>
            <div className="type-toggle">
              <button
                type="button"
                className={`opt${form.type === 'in' ? ' selected' : ''}`}
                onClick={() => setForm({ ...form, type: 'in' })}
              >
                Entrada
              </button>
              <button
                type="button"
                className={`opt${form.type === 'out' ? ' selected' : ''}`}
                onClick={() => setForm({ ...form, type: 'out' })}
              >
                Saída
              </button>
            </div>
          </div>
          <div className="field full">
            <label htmlFor="description">Descrição *</label>
            <input
              id="description"
              className={`input${errors.description ? ' error' : ''}`}
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Ex.: Pagamento escola, salário, venda"
            />
            {errors.description && <div className="field-error">{errors.description}</div>}
          </div>
          <div className="field">
            <label htmlFor="amount">Valor *</label>
            <input
              id="amount"
              className={`input${errors.amount ? ' error' : ''}`}
              inputMode="decimal"
              value={form.amount}
              onChange={e => setForm({ ...form, amount: e.target.value })}
              placeholder="0,00"
            />
            {errors.amount && <div className="field-error">{errors.amount}</div>}
          </div>
          <div className="field">
            <label htmlFor="movementDate">Data *</label>
            <input
              id="movementDate"
              type="date"
              className={`input${errors.movementDate ? ' error' : ''}`}
              value={form.movementDate}
              onChange={e => setForm({ ...form, movementDate: e.target.value })}
            />
            {errors.movementDate && <div className="field-error">{errors.movementDate}</div>}
          </div>
        </div>

        <MoreOptions>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="cashAccountId">Conta</label>
              <select
                id="cashAccountId"
                className="select"
                value={form.cashAccountId}
                onChange={e => setForm({ ...form, cashAccountId: e.target.value })}
              >
                <option value="">Padrão</option>
                {(accounts || []).map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="cashCategoryId">Categoria</label>
              <select
                id="cashCategoryId"
                className="select"
                value={form.cashCategoryId}
                onChange={e => setForm({ ...form, cashCategoryId: e.target.value })}
              >
                <option value="">—</option>
                {(categories || []).map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="field full">
              <label htmlFor="notes">Observações</label>
              <input
                id="notes"
                className="input"
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>
        </MoreOptions>
      </form>
    </FormPage>
  );
};

export default NovoLancamento;
